import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { NavBar } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Camera, Users, Star, MapPin, ArrowRight, Zap, FileText, Calendar, CheckCircle, HelpCircle, ChevronDown, Quote } from "lucide-react";
import React from "react";

var FAQ_ITEMS = [
  {
    q: "What is a second shooter and why do studios hire them?",
    a: "A second shooter is a photographer or videographer hired to assist the lead shooter at events like weddings, corporate shoots, and commercial projects. Studios hire second shooters to capture additional angles, cover more moments, and deliver a more complete final product to their clients. GottaSecond makes it easy to find experienced second shooters in your area.",
  },
  {
    q: "How do I find second shooter jobs near me?",
    a: "Create a free profile on GottaSecond, set your location and the roles you're available for (second photographer, videographer, assistant, drone operator, etc.), and studios in your area will discover you when they post jobs. You'll be matched based on your location, skills, and availability.",
  },
  {
    q: "What roles can I hire for on GottaSecond?",
    a: "GottaSecond supports hiring for photography and videography roles: Second Photographer, Second Videographer, Photo/Video Assistant, and Drone Operator. We focus on the roles that studios actually need for weddings and events.",
  },
  {
    q: "Is GottaSecond free for second shooters and assistants?",
    a: "Yes! Creating a profile and getting discovered by studios is completely free. There are no fees to receive job offers. Studios pay a subscription to access the full candidate pool and post jobs.",
  },
  {
    q: "How does the hiring process work?",
    a: "Studios post a job with the event details, date, location, and rate. Matching candidates appear in the candidate pool. The studio reviews portfolios and ratings, selects a candidate, and sends a hire offer. Once accepted, a contract is automatically generated for both parties to sign electronically.",
  },
  {
    q: "Can I request reviews from studios I've worked with outside of GottaSecond?",
    a: "Yes! GottaSecond lets you request reviews from anyone via email — even studios that don't have a GottaSecond account yet. They can leave a review without signing up. This helps you build your reputation and get discovered by more studios.",
  },
  {
    q: "How are payments and contracts handled?",
    a: "GottaSecond provides built-in contract generation with e-signatures and secure payment processing through Stripe. Once a hire is confirmed, a detailed contract is generated covering the job details, rate, and terms. Payments are processed securely through the platform.",
  },
  {
    q: "What areas does GottaSecond cover?",
    a: "GottaSecond is available nationwide across the United States. Whether you're in a major metro area or a smaller market, you can post jobs and find talent based on your specific location and radius.",
  },
];

var ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "GottaSecond",
  "legalName": "Studio Lifesaver LLC",
  "url": "https://gottasecond.com",
  "logo": "https://customer-assets.emergentagent.com/job_20a21bfd-b8fc-46d5-80c1-3ef1449007db/artifacts/gnn6v1fg_Gotta%20Second-white.png",
  "description": "Marketplace connecting studios and photographers with vetted second shooters and assistants for weddings, events, and commercial shoots.",
  "sameAs": [],
  "contactPoint": {
    "@type": "ContactPoint",
    "email": "support@gottasecond.com",
    "contactType": "customer support",
  },
};

var WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "GottaSecond",
  "url": "https://gottasecond.com",
  "description": "Find and hire second shooters for photography and videography",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://gottasecond.com/jobs?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

var FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQ_ITEMS.map(function(item) {
    return {
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a,
      },
    };
  }),
};

var COMBINED_JSONLD = [ORGANIZATION_JSONLD, WEBSITE_JSONLD, FAQ_JSONLD];

function FAQItem({ question, answer }) {
  var [open, setOpen] = React.useState(false);
  return (
    <div className="py-5" data-testid="faq-item">
      <button
        onClick={function() { setOpen(!open); }}
        className="w-full flex items-start justify-between text-left gap-4 group"
        aria-expanded={open}
      >
        <h3 className="text-base font-semibold text-white group-hover:text-white/90 transition-colors pr-4">{question}</h3>
        <ChevronDown className={"w-5 h-5 text-white/40 shrink-0 mt-0.5 transition-transform duration-200 " + (open ? "rotate-180" : "")} />
      </button>
      {open && (
        <p className="mt-3 text-white/50 text-sm leading-relaxed pr-12">{answer}</p>
      )}
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <SEO
        title="Find & Hire Second Shooters for Photography & Videography"
        description="GottaSecond connects studios and photographers with vetted second shooters and assistants for weddings, events, and commercial shoots. Post jobs, hire talent, and manage contracts in one platform."
        path="/"
        jsonLd={COMBINED_JSONLD}
      />
      {/* Navigation */}
      <NavBar>
        <a href="/articles" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider" data-testid="nav-articles">
          Articles
        </a>
        <Link to="/pricing" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider" data-testid="nav-pricing">
          Pricing
        </Link>
        <Link to="/help" className="text-sm font-medium text-white/60 hover:text-white transition-colors" data-testid="nav-help-landing">
          <HelpCircle className="w-5 h-5" />
        </Link>
        <Link to="/login" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider" data-testid="nav-login">
          Login
        </Link>
        <Link to="/register">
          <Button className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-6 py-2 uppercase tracking-wide text-sm" data-testid="nav-register">
            Get Started
          </Button>
        </Link>
      </NavBar>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1664806223123-c3d01da422da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHx3ZWRkaW5nJTIwcGhvdG9ncmFwaGVyJTIwd2l0aCUyMGNhbWVyYSUyMHNob290aW5nJTIwY291cGxlfGVufDB8fHx8MTc3NTc1MDg3MHww&ixlib=rb-4.1.0&q=85"
            alt="Photographer capturing a wedding couple at sunset"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-32 pb-20">
          <div className="max-w-3xl">
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-white/50 font-mono mb-6">
              For Photographers & Videographers
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6 animate-fade-in">
              Stop Scrolling Facebook Groups
              <br />
              <span className="text-white/80">for Second Shooters</span>
            </h1>
            <p className="text-base md:text-lg text-white/60 leading-relaxed max-w-xl mb-8">
              The platform built for real photographers. Find vetted second shooters in your area, manage contracts, and stop starting from scratch for every job.
            </p>

            {/* Free callout */}
            <div className="inline-flex items-center gap-2 bg-coral-dim border border-coral/30 outline outline-2 outline-[#ff6b6b] outline-offset-2 rounded-full px-5 py-2 mb-8">
              <CheckCircle className="w-4 h-4 text-coral" />
              <span className="text-coral font-medium text-sm">Always FREE for second shooters</span>
              <span className="text-white/50 text-sm hidden sm:inline">— get discovered and hired at no cost</span>
            </div>

            <div className="flex flex-wrap gap-4">
              <Link to="/register?role=studio">
                <Button className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8 py-4 uppercase tracking-wide text-sm btn-hover" data-testid="hero-get-started-studio">
                  I Hire Second Shooters <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/register?role=assistant">
                <Button className="bg-coral text-white hover:bg-coral/90 rounded-sm font-bold px-8 py-4 uppercase tracking-wide text-sm btn-hover" data-testid="hero-get-started-assistant">
                  I Am a Second Shooter
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/40 rounded-full" />
          </div>
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="py-24 md:py-32 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-white/50 font-mono">The Challenge</span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mt-4 mb-6">
              Finding Reliable Help Has Been Hard
            </h2>
            <p className="text-white/50 text-lg max-w-3xl mx-auto">
              The photography industry has lacked a dedicated platform for connecting studios with skilled second shooters.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 md:gap-16">
            {/* For Studios - The Old Way */}
            <div className="gs-card p-8 rounded-lg border border-white/10">
              <h3 className="text-xl font-semibold mb-4 text-[#3498DB]">For Studios - The Old Way</h3>
              <ul className="space-y-4 text-white/60">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-white/30 rounded-full mt-2 shrink-0" />
                  <span>Posting in Facebook groups, hoping for responses, and starting from scratch with every single job</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-white/30 rounded-full mt-2 shrink-0" />
                  <span>No way to verify skills or reliability before hiring</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-white/30 rounded-full mt-2 shrink-0" />
                  <span>Asking the same questions to many people, looking at portfolios across platforms</span>
                </li>
              </ul>
            </div>

            {/* For Second Shooters - The Old Way */}
            <div className="gs-card p-8 rounded-lg border border-white/10">
              <h3 className="text-xl font-semibold mb-4 text-coral">For Second Shooters - The Old Way</h3>
              <ul className="space-y-4 text-white/60">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-white/30 rounded-full mt-2 shrink-0" />
                  <span>Constantly monitoring Facebook groups and forums for opportunities</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-white/30 rounded-full mt-2 shrink-0" />
                  <span>Getting lost in a sea of responses with no way to stand out</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-white/30 rounded-full mt-2 shrink-0" />
                  <span>No way to build lasting credibility or showcase your track record</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* For Studios / For Assistants Section - The Solution (No Images) */}
      <section className="py-16 md:py-20 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.3em] text-white/50 font-mono">The Solution</span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mt-4">
              A Better Way to Connect
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 md:gap-24">
            {/* For Studios */}
            <div className="relative">
              <span className="text-xs uppercase tracking-[0.3em] text-[#3498DB] font-mono">For Studios</span>
              <h3 className="text-2xl md:text-3xl font-semibold mt-2 mb-4">
                Find the Perfect Second Shooter
              </h3>
              <ul className="space-y-3 text-white/60 mb-6">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-[#3498DB] rounded-full mt-2 shrink-0" />
                  <span>Post jobs with specific requirements</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-[#3498DB] rounded-full mt-2 shrink-0" />
                  <span>10+ point matching criteria so you see candidates that match your style and needs</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-[#3498DB] rounded-full mt-2 shrink-0" />
                  <span>Quickly see who is available on your date</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-[#3498DB] rounded-full mt-2 shrink-0" />
                  <span>See complete profiles with reviews, portfolios and gear lists</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-[#3498DB] rounded-full mt-2 shrink-0" />
                  <span>Automate the contract process for new team members</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-[#3498DB] rounded-full mt-2 shrink-0" />
                  <span>Review your contract and easily get invoices and send payments</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-[#3498DB] rounded-full mt-2 shrink-0" />
                  <span>Only pay for jobs where you get valid results</span>
                </li>
              </ul>
              <Link to="/register?role=studio">
                <Button className="bg-[#3498DB] text-white hover:bg-[#2980B9] rounded-sm font-bold px-6 py-3 uppercase tracking-wide text-sm" data-testid="cta-studio">
                  Register as Studio
                </Button>
              </Link>
            </div>

            {/* For Assistants */}
            <div className="relative">
              <span className="text-xs uppercase tracking-[0.3em] text-coral font-mono">For Assistants</span>
              <h3 className="text-2xl md:text-3xl font-semibold mt-2 mb-4">
                Get Hired for Your Skills
              </h3>
              <ul className="space-y-3 text-white/60 mb-6">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-coral rounded-full mt-2 shrink-0" />
                  <span>Showcase your portfolio</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-coral rounded-full mt-2 shrink-0" />
                  <span>Get matched to the best jobs in your area</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-coral rounded-full mt-2 shrink-0" />
                  <span>List your gear and skills</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-coral rounded-full mt-2 shrink-0" />
                  <span>Connect your calendar so you don't waste time with jobs you're not available for</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-coral rounded-full mt-2 shrink-0" />
                  <span>Set your rates and travel radius</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-coral rounded-full mt-2 shrink-0" />
                  <span>Build your reputation with reviews</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-coral rounded-full mt-2 shrink-0" />
                  <span>Get matched for free — or subscribe to see all jobs in your area and reach out to studios</span>
                </li>
              </ul>
              <Link to="/register?role=assistant">
                <Button className="bg-coral text-white hover:bg-coral/90 rounded-sm font-bold px-6 py-3 uppercase tracking-wide text-sm" data-testid="cta-assistant">
                  Register as Assistant
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Built for Creatives Section (All white icons) */}
      <section className="relative py-24 md:py-32 bg-[#0a0a0a] border-t border-white/5 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1711473610014-0505db6f6702?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxwaG90b2dyYXBoZXIlMjB3b3JraW5nJTIwZXZlbnQlMjBwcm9mZXNzaW9uYWwlMjBEU0xSfGVufDB8fHx8MTc3NTc1MDg3MXww&ixlib=rb-4.1.0&q=85"
            alt="Professional photographer working at an event"
            className="w-full h-full object-cover opacity-35 grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]" />
        </div>
        
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-white/50 font-mono">How It Works</span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mt-4">
              Built for Creatives
            </h2>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Smart Matching */}
            <div className="gs-card p-8 rounded-lg group card-hover" data-testid="feature-matching">
              <div className="w-12 h-12 bg-white/5 rounded-sm flex items-center justify-center mb-6 group-hover:bg-white/10 transition-colors">
                <Zap className="w-6 h-6 text-white/80" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Smart Matching</h3>
              <p className="text-white/50 leading-relaxed">
                Automatically matches the right candidates to the right jobs based on style, gear, experience, availability, area, and more.
              </p>
            </div>

            {/* Automated Contracts */}
            <div className="gs-card p-8 rounded-lg group card-hover" data-testid="feature-contracts">
              <div className="w-12 h-12 bg-white/5 rounded-sm flex items-center justify-center mb-6 group-hover:bg-white/10 transition-colors">
                <FileText className="w-6 h-6 text-white/80" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Automated Contracts</h3>
              <p className="text-white/50 leading-relaxed">
                Studios keep second shooting contracts on file for easy automated process for signing new seconds.
              </p>
            </div>

            {/* Simple Booking */}
            <div className="gs-card p-8 rounded-lg group card-hover" data-testid="feature-booking">
              <div className="w-12 h-12 bg-white/5 rounded-sm flex items-center justify-center mb-6 group-hover:bg-white/10 transition-colors">
                <Calendar className="w-6 h-6 text-white/80" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Simple Booking</h3>
              <p className="text-white/50 leading-relaxed">
                Post your job requirements, review applicants, and hire the perfect match. 
                All in one platform.
              </p>
            </div>

            {/* Browse Portfolios */}
            <div className="gs-card p-8 rounded-lg group card-hover" data-testid="feature-portfolios">
              <div className="w-12 h-12 bg-white/5 rounded-sm flex items-center justify-center mb-6 group-hover:bg-white/10 transition-colors">
                <Camera className="w-6 h-6 text-white/80" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Browse Portfolios</h3>
              <p className="text-white/50 leading-relaxed">
                View work samples, gear lists, and verified reviews before making your decision. 
                Quality you can trust.
              </p>
            </div>

            {/* Two-Way Reviews */}
            <div className="gs-card p-8 rounded-lg group card-hover" data-testid="feature-reviews">
              <div className="w-12 h-12 bg-white/5 rounded-sm flex items-center justify-center mb-6 group-hover:bg-white/10 transition-colors">
                <Star className="w-6 h-6 text-white/80" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Two-Way Reviews</h3>
              <p className="text-white/50 leading-relaxed">
                Both studios and assistants can leave reviews after each job. 
                Build your reputation and find reliable collaborators.
              </p>
            </div>

            {/* Location-Based Search */}
            <div className="gs-card p-8 rounded-lg group card-hover" data-testid="feature-location">
              <div className="w-12 h-12 bg-white/5 rounded-sm flex items-center justify-center mb-6 group-hover:bg-white/10 transition-colors">
                <MapPin className="w-6 h-6 text-white/80" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Location-Based Search</h3>
              <p className="text-white/50 leading-relaxed">
                Set your search radius and find talent nearby. Whether you need someone for a local wedding 
                or a destination shoot, we've got you covered.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 md:py-28 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Quote className="w-10 h-10 text-coral/40 mx-auto mb-8" />
          <blockquote className="text-base md:text-lg text-white/70 leading-relaxed italic mb-8" data-testid="testimonial-quote">
            "I've worked on hundreds of weddings as an associate/second/assistant for other photographers and the hardest part always felt like finding the work. Facebook was clunky and often lacked details, showing my work was random social media posts that were difficult to track/share... the whole process was a real circus. But now with Gotta Second, I feel like I can easily see what work is out there, what matches my rate/experience, be protected with contracts, and easily invoice/track my billable. I'm way more excited about Gotta Second than I am about the newest cameras or gear."
          </blockquote>
          <div>
            <p className="text-white font-semibold" data-testid="testimonial-author">Skyler P.</p>
            <p className="text-white/40 text-sm">Second Shooter & Photo Assistant</p>
          </div>
        </div>
      </section>

      {/* Get Started in Seconds Section (All white circles) */}
      <section className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1742821855272-981ae713ebd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHx3ZWRkaW5nJTIwcGhvdG9ncmFwaGVyJTIwY2FtZXJhJTIwZGFyayUyMG1vb2R5fGVufDB8fHx8MTc3MTAyNDQ5M3ww&ixlib=rb-4.1.0&q=85"
            alt="Wedding photographer with camera capturing moments at an event"
            className="w-full h-full object-cover opacity-35 grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]" />
        </div>
        
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-white/50 font-mono">Quick Start</span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mt-4">
              Get Started in Seconds
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Create Your Profile</h3>
              <p className="text-white/50 leading-relaxed">
                Sign up free in under a minute. Add your portfolio, gear, skills, and set your travel radius so the right people find you.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Get Matched</h3>
              <p className="text-white/50 leading-relaxed">
                Our matching algorithm connects studios with the right second shooters based on location, style, gear, and availability. Both sides get notified when there's a fit.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Hire & Sign</h3>
              <p className="text-white/50 leading-relaxed">
                Review portfolios, message candidates, and send a hire offer. Contracts are generated and signed electronically — no more chasing paperwork.
              </p>
            </div>

            {/* Step 4 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">4</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Shoot & Review</h3>
              <p className="text-white/50 leading-relaxed">
                Do what you do best. After the job, leave a review to help the community and build your track record for next time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 md:py-32 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-white/50 font-mono">Pricing</span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mt-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-white/50 text-lg mt-4">
              Assistants get listed and hired for free. Studios pay only when they find the right match.
            </p>
          </div>

          {/* Free for Assistants Callout */}
          <div className="max-w-3xl mx-auto mb-12 text-center p-8 bg-white/[0.03] border border-white/10 rounded-lg">
            <h3 className="text-2xl font-bold mb-3">Second Shooters — Always Free</h3>
            <p className="text-white/60 mb-4">
              Create your profile, get matched to jobs, and get hired — all at no cost. Upgrade to Pro only if you want to browse and reach out to studios proactively.
            </p>
            <Link to="/register?role=assistant">
              <Button className="bg-coral text-white hover:bg-coral/90 rounded-sm font-bold px-6 py-3">
                Sign Up Free
              </Button>
            </Link>
          </div>

          {/* Pay Per Job for Studios */}
          <div className="max-w-3xl mx-auto mb-12 text-center p-8 bg-white/[0.03] border border-white/10 rounded-lg">
            <span className="text-xs uppercase tracking-[0.3em] text-white/50 font-mono">For Studios</span>
            <h3 className="text-2xl font-bold mt-2 mb-3">Pay Per Job — <span className="text-white">$10</span></h3>
            <p className="text-white/60 mb-4">
              Post a single job, view matched candidates, message them, and hire. Pay only when you post — no subscription required.
            </p>
            <Link to="/register?role=studio">
              <Button className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-6 py-3">
                Post a Job
              </Button>
            </Link>
          </div>

          {/* Subscription Plans Header */}
          <div className="text-center mb-8">
            <h3 className="text-xl font-semibold text-white/80">Monthly Subscriptions</h3>
            <p className="text-white/50">For power users who want unlimited access</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Studio Pro */}
            <div className="gs-card p-8 rounded-lg border border-white/10">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-white/80" />
                <span className="text-xs uppercase tracking-[0.3em] text-white/60 font-mono">For Studios</span>
              </div>
              <h3 className="text-2xl font-bold mb-4">Studio Pro</h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-bold">$19</span>
                <span className="text-white/50">/month</span>
              </div>
              <ul className="space-y-3 text-white/60 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-white/60 shrink-0" />
                  <span>Unlimited job postings</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-white/60 shrink-0" />
                  <span>View all candidates in your area</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-white/60 shrink-0" />
                  <span>Contract templates & e-signatures</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-white/60 shrink-0" />
                  <span>Direct messaging</span>
                </li>
              </ul>
              <Link to="/pricing" className="block">
                <Button className="w-full bg-white text-black hover:bg-gray-200 rounded-sm font-bold">
                  Get Studio Pro
                </Button>
              </Link>
            </div>

            {/* Second Shooter Pro */}
            <div className="gs-card p-8 rounded-lg border border-white/10">
              <div className="flex items-center gap-2 mb-4">
                <Camera className="w-5 h-5 text-white/80" />
                <span className="text-xs uppercase tracking-[0.3em] text-white/60 font-mono">For Assistants</span>
              </div>
              <h3 className="text-2xl font-bold mb-4">Second Shooter Pro</h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-bold">$9</span>
                <span className="text-white/50">/month</span>
              </div>
              <ul className="space-y-3 text-white/60 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-white/60 shrink-0" />
                  <span>Browse all job listings</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-white/60 shrink-0" />
                  <span>Message studios directly</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-white/60 shrink-0" />
                  <span>Priority in search results</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-white/60 shrink-0" />
                  <span>Email notifications for new jobs</span>
                </li>
              </ul>
              <Link to="/pricing" className="block">
                <Button className="w-full bg-white text-black hover:bg-gray-200 rounded-sm font-bold">
                  Get Pro
                </Button>
              </Link>
            </div>

            {/* All Access */}
            <div className="gs-card p-8 rounded-lg border border-white/10">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5 text-white/80" />
                <span className="text-xs uppercase tracking-[0.3em] text-white/60 font-mono">Best Value</span>
              </div>
              <h3 className="text-2xl font-bold mb-4">All Access</h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-bold">$25</span>
                <span className="text-white/50">/month</span>
              </div>
              <ul className="space-y-3 text-white/60 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-white/60 shrink-0" />
                  <span>Everything in Studio Pro</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-white/60 shrink-0" />
                  <span>Everything in Second Shooter Pro</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-white/60 shrink-0" />
                  <span>Hire and get hired</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-white/60 shrink-0" />
                  <span>Save $3/month vs. separate plans</span>
                </li>
              </ul>
              <Link to="/pricing" className="block">
                <Button className="w-full bg-white text-black hover:bg-gray-200 rounded-sm font-bold">
                  Get All Access
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6 md:px-12 border-t border-white/5" id="faq" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 tracking-tight">
            Frequently Asked Questions
          </h2>
          <p className="text-white/50 text-center mb-12 text-base md:text-lg">
            Everything you need to know about finding and hiring second shooters
          </p>
          <div className="space-y-0 divide-y divide-white/8">
            {FAQ_ITEMS.map(function(item, idx) {
              return <FAQItem key={idx} question={item.q} answer={item.a} />;
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-white/[0.03] border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            We're Building Something Better
          </h2>
          <p className="text-white/50 text-base md:text-lg max-w-xl mx-auto mb-10">
            GottaSecond is growing fast. Join the community of photographers who are done with Facebook groups and ready for a real platform.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register?role=studio">
              <Button className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-10 py-4 uppercase tracking-wide text-sm btn-hover" data-testid="cta-final-studio">
                Register as Studio <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/register?role=assistant">
              <Button className="bg-coral text-white hover:bg-coral/90 rounded-sm font-bold px-10 py-4 uppercase tracking-wide text-sm btn-hover" data-testid="cta-final-assistant">
                Join Free as Second Shooter
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
